import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, imgUrl, IMAGES } from "../lib/api";
import { Search, X, Instagram, Twitter, Youtube, Facebook, Music as MusicIcon, ExternalLink, Mic, ArrowUpRight } from "lucide-react";
import { slugify } from "../lib/slug";
import Seo from "../components/Seo";

const SOCIAL_DEFS = [
  { key: "instagram_url", label: "Instagram", Icon: Instagram, color: "#E4405F" },
  { key: "twitter_url", label: "X / Twitter", Icon: Twitter, color: "#FFFFFF" },
  { key: "youtube_url", label: "YouTube", Icon: Youtube, color: "#FF0033" },
  { key: "spotify_url", label: "Spotify", Icon: MusicIcon, color: "#1DB954" },
  { key: "apple_music_url", label: "Apple Music", Icon: MusicIcon, color: "#FA243C" },
  { key: "tiktok_url", label: "TikTok", Icon: MusicIcon, color: "#FFFFFF" },
  { key: "soundcloud_url", label: "SoundCloud", Icon: MusicIcon, color: "#FF5500" },
  { key: "facebook_url", label: "Facebook", Icon: Facebook, color: "#1877F2" },
  { key: "website_url", label: "Website", Icon: ExternalLink, color: "#FCA311" },
];

// Renders an artist's social links as small glowing circles orbiting around
// a central circle. Returns null if there are no social links.
function SocialOrbit({ artist, radius = 150, size = 56 }) {
  const links = SOCIAL_DEFS.filter((s) => artist[s.key]);
  if (links.length === 0) return null;
  // Start from top (-90deg) and distribute evenly
  const step = (Math.PI * 2) / links.length;
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="artist-social-orbit">
      {links.map((s, i) => {
        const angle = -Math.PI / 2 + i * step;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        const { Icon, color, label, key } = s;
        return (
          <a
            key={key}
            href={artist[key]}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            title={label}
            className="absolute left-1/2 top-1/2 rounded-full bg-black/80 backdrop-blur flex items-center justify-center transition-all duration-300 pointer-events-auto hover:scale-125 group/orbit"
            style={{
              width: size,
              height: size,
              "--orbit-x": `${x}px`,
              "--orbit-y": `${y}px`,
              transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
              border: `2px solid ${color}`,
              boxShadow: `0 0 18px ${color}, 0 0 36px ${color}66, inset 0 0 10px ${color}33`,
              animation: `orbit-pop 380ms cubic-bezier(.2,1.4,.4,1) both`,
              animationDelay: `${i * 60}ms`,
            }}
            data-testid={`artist-orbit-${key}`}
          >
            <Icon className="w-5 h-5" style={{ color }} />
            <span
              className="absolute -bottom-6 left-1/2 -translate-x-1/2 font-mono text-[9px] tracking-[0.2em] text-white/0 group-hover/orbit:text-white whitespace-nowrap transition-colors"
            >
              {label.toUpperCase()}
            </span>
          </a>
        );
      })}
    </div>
  );
}

// Big glowing circular avatar with name underneath.
function ArtistCircle({ artist, onClick, glowColor = "#E63946" }) {
  const initials = artist.name ? artist.name.slice(0, 1).toUpperCase() : "?";
  return (
    <button
      onClick={onClick}
      className="group flex flex-col items-center gap-4 focus:outline-none"
      data-testid={`artist-card-${artist.id}`}
    >
      <div
        className="relative rounded-full overflow-hidden transition-all duration-500 group-hover:scale-105 group-hover:-translate-y-1"
        style={{
          width: 220,
          height: 220,
          border: `3px solid ${glowColor}`,
          boxShadow: `0 0 24px ${glowColor}, 0 0 60px ${glowColor}55, 0 20px 60px rgba(0,0,0,0.55)`,
        }}
      >
        {/* breathing halo behind the disc */}
        <div
          aria-hidden="true"
          className="absolute -inset-3 rounded-full breathe pointer-events-none opacity-40 group-hover:opacity-90 transition-opacity"
          style={{ background: `radial-gradient(circle, ${glowColor}66 0%, transparent 70%)` }}
        />
        {artist.photo_path ? (
          <img
            src={imgUrl(artist.photo_path)}
            alt={artist.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-display text-7xl text-white/30 bg-black">
            {initials}
          </div>
        )}
        <div
          className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ boxShadow: `inset 0 0 60px ${glowColor}99` }}
        />
      </div>
      <div className="text-center">
        <div className="font-mono text-[10px] tracking-[0.3em] text-[#FCA311]">{artist.role || "ARTIST"}</div>
        <div className="font-display text-2xl mt-1 group-hover:text-[#E63946] transition-colors">{artist.name}</div>
      </div>
    </button>
  );
}

export default function Artists() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data || [])).finally(() => setLoading(false));
  }, []);

  // If url has ?open=<id>, auto-open that artist's constellation once data loads
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || artists.length === 0) return;
    const match = artists.find((a) => a.id === openId);
    if (match) setSelected(match);
  }, [artists, searchParams]);

  // If url has ?q=<name>, prefill the search field
  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setQuery(q);
  }, [searchParams]);

  // Esc to close
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === "Escape") closeModal(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const closeModal = () => {
    setSelected(null);
    if (searchParams.get("open")) {
      const sp = new URLSearchParams(searchParams);
      sp.delete("open");
      setSearchParams(sp, { replace: true });
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return artists;
    return artists.filter((a) =>
      (a.name || "").toLowerCase().includes(q) ||
      (a.role || "").toLowerCase().includes(q) ||
      (a.bio || "").toLowerCase().includes(q)
    );
  }, [artists, query]);

  return (
    <div className="relative bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="artists-page">
      <Seo
        title="Artists | SyndaFam — Independent Canadian Hip-Hop Roster"
        description="Meet the SyndaFam roster — the rappers, singers, producers, engineers and creators behind the Toronto hip-hop collective's music, videos and independent Canadian artist releases."
        path="/artists"
      />
      {/* Living aurora + drifting orbs */}
      <div className="fixed inset-0 -z-10 bg-aurora pointer-events-none opacity-70" aria-hidden="true" />
      <div className="fixed inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="orb orb-red orb-live"   style={{ width: 620, height: 620, top: "-160px", left: "-180px" }} />
        <div className="orb orb-amber orb-live" style={{ width: 520, height: 520, bottom: "-160px", right: "-140px", animationDelay: "-6s" }} />
        <div className="orb orb-cyan orb-live"  style={{ width: 360, height: 360, top: "40%", left: "40%", opacity: 0.18, animationDelay: "-3s" }} />
      </div>

      {/* keyframes for orbit pop-in */}
      <style>{`
        @keyframes orbit-pop {
          0%   { opacity: 0; transform: translate(-50%, -50%) translate(0px, 0px) scale(0.2); }
          70%  { opacity: 1; }
          100% { opacity: 1; transform: translate(-50%, -50%) translate(var(--orbit-x), var(--orbit-y)) scale(1); }
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 30px #E63946, 0 0 80px #E6394666; }
          50%      { box-shadow: 0 0 50px #E63946, 0 0 120px #E6394699; }
        }
      `}</style>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${IMAGES.texture})`, backgroundSize: "cover" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-10 pt-24 pb-20 lg:py-28">
          <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-4 text-glow-red reveal reveal-1">// THE ROSTER</div>
          <h1 className="font-display leading-[0.85] tracking-tighter flex items-center gap-4 flex-wrap reveal reveal-2" style={{ fontSize: "clamp(3rem, 10vw, 9rem)" }}>
            <Mic className="w-14 h-14 sm:w-20 sm:h-20 text-[#FCA311]" style={{ filter: "drop-shadow(0 0 24px rgba(252,163,17,0.55))" }} />
            <span>ARTISTS<span className="text-[#E63946] text-glow-red">.</span></span>
          </h1>
          <div className="mt-8 max-w-xl glass-card-strong rounded-3xl p-6 reveal reveal-3">
            <p className="text-white/85 text-base sm:text-lg leading-relaxed">
              The whole Fam, one search away. Tap a circle to light up their links.
            </p>
          </div>

          {/* Search bar */}
          <div className="mt-10 max-w-2xl reveal reveal-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="SEARCH ARTISTS, ROLES, OR VIBES…"
                className="w-full glass-card rounded-2xl border-transparent focus:border-[#E63946] outline-none pl-12 pr-12 py-4 font-mono text-sm tracking-wider placeholder:text-white/30 transition-colors"
                data-testid="artists-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:text-[#E63946] z-10"
                  aria-label="Clear"
                  data-testid="artists-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-widest text-white/40">
              {filtered.length} / {artists.length} ARTISTS
            </div>
          </div>
        </div>
      </section>

      {/* RESULTS — circles */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 py-16">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-10 justify-items-center">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-full glass-card animate-pulse"
                style={{ width: 220, height: 220 }}
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="font-display text-3xl py-20 text-center text-white/40" data-testid="artists-empty">
            {artists.length === 0 ? "NO ARTISTS YET. CHECK BACK SOON." : "NO MATCHES."}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-14 justify-items-center"
            data-testid="artists-grid"
          >
            {filtered.map((a) => (
              <ArtistCircle key={a.id} artist={a} onClick={() => setSelected(a)} />
            ))}
          </div>
        )}
      </section>

      {/* CONSTELLATION OVERLAY */}
      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-5 overflow-y-auto"
          onClick={closeModal}
          data-testid="artist-profile-modal"
        >
          {/* Ambient orbs behind the modal */}
          <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
            <div className="orb orb-red" style={{ width: 500, height: 500, top: "-80px", left: "10%", opacity: 0.35 }} />
            <div className="orb orb-amber" style={{ width: 500, height: 500, bottom: "-100px", right: "10%", opacity: 0.3 }} />
          </div>
          <button
            onClick={closeModal}
            className="fixed top-5 right-5 z-[101] w-10 h-10 rounded-full glass-card flex items-center justify-center text-white hover:text-[#E63946] transition-colors"
            aria-label="Close"
            data-testid="artist-profile-close"
          >
            <X className="w-5 h-5" />
          </button>

          <div
            onClick={(e) => e.stopPropagation()}
            className="relative flex flex-col items-center"
            style={{ width: "100%", maxWidth: 760 }}
          >
            {/* role + name on top */}
            <div className="text-center mb-8">
              <div className="font-mono text-[10px] tracking-[0.5em] text-[#FCA311]" data-testid="artist-profile-role">
                {selected.role || "ARTIST"}
              </div>
              <h2 className="font-display text-5xl sm:text-6xl mt-2 leading-none" data-testid="artist-profile-name">
                {selected.name}
              </h2>
            </div>

            {/* central avatar + orbiting socials */}
            <div
              className="relative flex items-center justify-center"
              style={{ width: 460, height: 460, maxWidth: "92vw", maxHeight: "92vw" }}
            >
              <div
                className="relative rounded-full overflow-hidden"
                style={{
                  width: 260,
                  height: 260,
                  maxWidth: "60vw",
                  maxHeight: "60vw",
                  border: "3px solid #E63946",
                  animation: "pulse-glow 2.4s ease-in-out infinite",
                }}
                data-testid="artist-profile-avatar"
              >
                {selected.photo_path ? (
                  <img src={imgUrl(selected.photo_path)} alt={selected.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display text-[8rem] text-white/20 bg-black">
                    {selected.name.slice(0, 1).toUpperCase()}
                  </div>
                )}
              </div>
              <SocialOrbit artist={selected} radius={180} size={62} />
            </div>

            {/* bio */}
            {selected.bio && (
              <p
                className="text-white/70 text-sm sm:text-base mt-10 max-w-xl text-center whitespace-pre-wrap leading-relaxed"
                data-testid="artist-profile-bio"
              >
                {selected.bio}
              </p>
            )}

            {/* Deep link into the full artist profile page (SEO + track catalogue) */}
            <Link
              to={`/artists/${slugify(selected.name)}`}
              onClick={closeModal}
              className="mt-8 inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#C92A36] text-white font-mono text-xs tracking-[0.35em] px-6 py-3 shadow-[0_0_30px_rgba(230,57,70,0.55)] transition-colors"
              data-testid="artist-profile-open-page"
            >
              OPEN FULL PROFILE
              <ArrowUpRight className="w-4 h-4" />
            </Link>

            {/* empty state if no socials */}
            {SOCIAL_DEFS.every((s) => !selected[s.key]) && (
              <div className="mt-6 font-mono text-[10px] tracking-widest text-white/40">
                NO LINKS YET — STAY TUNED.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
